import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useSocket } from '../../realtime/context/SocketContext';
import { useAuth } from '../../auth/context/AuthContext';

function normalizeConversation(user) {
  return {
    id: user.id,
    full_name: user.full_name,
    shipping_mark: user.shipping_mark || '',
    email: user.email || '',
    last_message: user.last_message || '',
    last_message_time: user.last_message_time || null,
    unread_count: user.unread_count || 0,
  };
}

export default function AdminChats() {
  const { user } = useAuth();
  const socket = useSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const bottomRef = useRef(null);

  const loadConvos = () =>
    axios
      .get('/api/chat/conversations')
      .then((response) => {
        setConvos((previous) => {
          const next = response.data.map(normalizeConversation);
          if (active && !next.some((conversation) => conversation.id === active.id)) {
            return [normalizeConversation(active), ...next];
          }
          return next;
        });
      })
      .catch(() => {});

  useEffect(() => {
    loadConvos();
  }, []);

  const openChat = (conversation) => {
    const normalized = normalizeConversation(conversation);
    setActive(normalized);
    setConvos((previous) => {
      if (previous.some((item) => item.id === normalized.id)) {
        return previous.map((item) => (item.id === normalized.id ? { ...item, unread_count: 0 } : item));
      }
      return [normalized, ...previous];
    });

    axios
      .get(`/api/chat/messages/${normalized.id}`)
      .then((response) => setMessages(response.data))
      .catch(() => setMessages([]));

    socket?.emit('chat:read', { senderId: normalized.id });
  };

  useEffect(() => {
    const startUser = location.state?.startUser;
    if (!startUser?.id) {
      return;
    }

    openChat(startUser);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleMessage = (message) => {
      const counterpartId = message.sender_id === user?.id ? message.receiver_id : message.sender_id;

      if (active && counterpartId === active.id) {
        setMessages((previous) => [...previous, message]);
        if (message.sender_id === active.id) {
          socket.emit('chat:read', { senderId: active.id });
        }
      }

      setConvos((previous) => {
        const index = previous.findIndex((conversation) => conversation.id === counterpartId);

        if (index === -1) {
          if (active && active.id === counterpartId) {
            const seeded = {
              ...normalizeConversation(active),
              last_message: message.message,
              last_message_time: message.sent_at,
              unread_count: 0,
            };
            return [seeded, ...previous];
          }

          loadConvos();
          return previous;
        }

        const next = [...previous];
        const current = next[index];
        next[index] = {
          ...current,
          last_message: message.message,
          last_message_time: message.sent_at,
          unread_count:
            message.sender_id === counterpartId && (!active || active.id !== counterpartId)
              ? (current.unread_count || 0) + 1
              : 0,
        };

        const [updated] = next.splice(index, 1);
        return [updated, ...next];
      });
    };

    socket.on('chat:message', handleMessage);
    return () => socket.off('chat:message', handleMessage);
  }, [socket, active, user?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = () => {
    const message = text.trim();
    if (!message || !active) return;
    if (!socket?.connected) {
      toast.error('Chat is reconnecting. Please try again.');
      return;
    }

    socket.emit('chat:send', { receiverId: active.id, message });
    setText('');
  };

  return (
    <div className="chat-page">
      <div className="page-header">
        <Link to="/admin" className="back-btn" aria-label="Back to dashboard">
          &lt;
        </Link>
        <div>
          <h1>Live Chats</h1>
          <p style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Powered by Smaiz Innovations</p>
        </div>
      </div>

      {!active ? (
        <div className="page-content">
          {convos.length === 0 ? (
            <div className="empty-state">
              <div className="icon">Chat</div>
              <p>No conversations yet</p>
            </div>
          ) : (
            convos.map((conversation) => (
              <div
                key={conversation.id}
                className="card"
                onClick={() => openChat(conversation)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: 'var(--surface-alt)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {conversation.full_name?.slice(0, 2)?.toUpperCase() || 'US'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontWeight: 700, fontSize: 14 }}>{conversation.full_name}</p>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'var(--muted)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {conversation.last_message || 'Start a conversation'}
                    </p>
                  </div>
                  {conversation.unread_count > 0 && (
                    <span
                      style={{
                        background: 'var(--danger)',
                        color: 'white',
                        fontSize: 11,
                        fontWeight: 700,
                        minWidth: 20,
                        height: 20,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 6px',
                      }}
                    >
                      {conversation.unread_count}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              background: 'var(--surface)',
              borderBottom: '1px solid var(--border)',
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <button onClick={() => setActive(null)} className="back-btn" type="button" aria-label="Back to conversations">
              &lt;
            </button>
            <p style={{ fontWeight: 700 }}>
              {active.full_name}{' '}
              {active.shipping_mark ? (
                <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 12 }}>
                  ({active.shipping_mark})
                </span>
              ) : null}
            </p>
          </div>

          <div className="chat-scroll">
            {messages.length === 0 ? (
              <div className="empty-state">
                <div className="icon">Chat</div>
                <p>No messages yet. Send the first message to start this conversation.</p>
              </div>
            ) : (
              messages.map((message) => {
                const isBot = message.sender_type === 'bot';
                const mine = message.sender_id === user?.id && !isBot;
                return (
                  <div
                    key={message.id || `${message.sender_id}-${message.sent_at}`}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}
                  >
                    {isBot && (
                      <p style={{ fontSize: 10, fontWeight: 700, color: '#1A6B8A', marginBottom: 3, marginLeft: 4, letterSpacing: 0.3 }}>
                        BOT | POWERED BY SMAIZ INNOVATIONS
                      </p>
                    )}
                    <div className={`chat-bubble chat-msg-enter ${mine ? 'mine' : isBot ? 'bot' : 'theirs'}`}>
                      {message.message.split('\n').map((line, li) => (
                        <span key={li}>
                          {line.split(/\*(.*?)\*/g).map((part, pi) =>
                            pi % 2 === 1 ? <strong key={pi}>{part}</strong> : part
                          )}
                          {li < message.message.split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </div>
                    {isBot && message.bot_meta?.options && (
                      <div className="bot-options bot-options--readonly">
                        {message.bot_meta.options.map((opt) => (
                          <span key={opt.id} className="bot-option-badge">{opt.label}</span>
                        ))}
                      </div>
                    )}
                    <p style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2, marginBottom: 6 }}>
                      {new Date(message.sent_at).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <div style={{ padding: '0 16px 8px', fontSize: 11, color: 'var(--muted)', textAlign: 'center' }}>
            Chat operations powered by Smaiz Innovations
          </div>

          <div className="chat-composer">
            <input
              className="input-field"
              style={{ flex: 1 }}
              placeholder="Type reply..."
              value={text}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && send()}
            />
            <button className="btn btn-primary" onClick={send} disabled={!text.trim()}>
              Send
            </button>
          </div>
        </>
      )}
    </div>
  );
}
